/**
 * shared/danhSachMucTieu.js: danh sách quỹ mục tiêu của founder.
 *
 * Dữ liệu nằm ngay trong `workspace.saved` (mảng nguồn vốn đã lưu có từ trước),
 * mỗi mục thêm vài trường để theo dõi tiếp cận. Không tạo kho mới, nên đồng bộ
 * tài khoản, sao lưu và hồ sơ một trang dùng được ngay. Mục cũ chỉ có { id, name }
 * vẫn hợp lệ: các trường mới đều không bắt buộc.
 *
 * Vì sao có giới hạn: bài Playbook "Bản đồ ba tầng gọi vốn" khuyên chọn 15 đến
 * 20 nhà đầu tư thay vì gặp ai cũng pitch. Quá 20 thì nhắc; quá TRAN thì chặn,
 * vì lúc đó danh sách không còn là danh sách mục tiêu nữa.
 *
 * Hàm thuần, không đụng localStorage hay giao diện.
 */

export const GOI_Y_TOI_DA = 20;
export const TRAN = 30;

/** Đợt tiếp cận: luyện câu chuyện với nơi ít ưu tiên trước, nơi muốn nhất sau cùng. */
export const DOT = {
  1: { nhan: 'Đợt 1', moTa: 'Luyện câu chuyện, lấy phản hồi' },
  2: { nhan: 'Đợt 2', moTa: 'Đã sửa deck theo phản hồi' },
  3: { nhan: 'Đợt 3', moTa: 'Nơi bạn muốn nhất' },
};

export const TRANG_THAI = {
  'chua-tiep-can': 'Chưa tiếp cận',
  'tim-gioi-thieu': 'Đang tìm người giới thiệu',
  'da-gui': 'Đã gửi hồ sơ',
  'da-gap': 'Đã gặp',
  'tham-dinh': 'Đang thẩm định',
  'tu-choi': 'Từ chối',
  'tam-dung': 'Tạm dừng',
};

/** Trạng thái còn đang theo đuổi (không tính từ chối, tạm dừng). */
const DANG_THEO = new Set(['chua-tiep-can', 'tim-gioi-thieu', 'da-gui', 'da-gap', 'tham-dinh']);

const TRAN_CHU = { nguoiGioiThieu: 120, ghiChu: 500, name: 160 };

const chuoi = (v, max) => String(v ?? '').trim().slice(0, max);
const laNgay = (v) => typeof v === 'string' && Number.isFinite(Date.parse(v));

/** Chuẩn hoá một mục. Mục cũ { id, name } được giữ nguyên ý nghĩa. */
export function chuanHoaMuc(x) {
  if (!x || typeof x !== 'object' || typeof x.id !== 'string') return null;
  const muc = { id: x.id, name: chuoi(x.name, TRAN_CHU.name) || x.id };
  if ([1, 2, 3].includes(Number(x.dot))) muc.dot = Number(x.dot);
  if (x.trangThai in TRANG_THAI) muc.trangThai = x.trangThai;
  const gt = chuoi(x.nguoiGioiThieu, TRAN_CHU.nguoiGioiThieu);
  if (gt) muc.nguoiGioiThieu = gt;
  const gc = chuoi(x.ghiChu, TRAN_CHU.ghiChu);
  if (gc) muc.ghiChu = gc;
  if (laNgay(x.them)) muc.them = x.them;
  if (laNgay(x.updated)) muc.updated = x.updated;
  return muc;
}

/** Mục này có hợp lệ để lưu không (dùng trong validateWorkspace). */
export function mucHopLe(x) {
  if (!x || typeof x !== 'object' || typeof x.id !== 'string' || typeof x.name !== 'string') return false;
  if (x.dot !== undefined && ![1, 2, 3].includes(x.dot)) return false;
  if (x.trangThai !== undefined && !(x.trangThai in TRANG_THAI)) return false;
  for (const [k, tran] of Object.entries(TRAN_CHU)) {
    if (x[k] !== undefined && (typeof x[k] !== 'string' || x[k].length > tran)) return false;
  }
  for (const k of ['them', 'updated']) if (x[k] !== undefined && !laNgay(x[k])) return false;
  return true;
}

export const coTrongDanhSach = (ds, id) => (ds || []).some((x) => x.id === id);

/**
 * Thêm một quỹ. Trả về danh sách mới; ném lỗi khi đã chạm trần.
 * Mặc định đợt 2: người dùng tự kéo lên đợt 3 những nơi muốn nhất.
 */
export function themMuc(ds, { id, name }, luc = new Date().toISOString()) {
  const cu = ds || [];
  if (coTrongDanhSach(cu, id)) return cu;
  if (cu.length >= TRAN) {
    throw new Error(`Danh sách đã có ${TRAN} quỹ. Bỏ bớt những nơi ít phù hợp trước khi thêm, vì gửi tràn lan làm giảm cơ hội chứ không tăng.`);
  }
  return [...cu, { id, name: chuoi(name, TRAN_CHU.name) || id, dot: 2, trangThai: 'chua-tiep-can', them: luc, updated: luc }];
}

export function boMuc(ds, id) {
  return (ds || []).filter((x) => x.id !== id);
}

export function capNhatMuc(ds, id, patch, luc = new Date().toISOString()) {
  return (ds || []).map((x) => (x.id === id ? chuanHoaMuc({ ...x, ...patch, updated: luc }) : x));
}

/** Sắp xếp để hiển thị: đợt 1 trước (làm trước), trong đợt theo thời gian thêm. */
export function sapXep(ds) {
  const thuTu = (x) => (x.dot || 2);
  return [...(ds || [])].sort((a, b) => thuTu(a) - thuTu(b)
    || String(a.them || '').localeCompare(String(b.them || '')));
}

export function thongKe(ds) {
  const kq = { tong: 0, dangTheo: 0, theoDot: { 1: 0, 2: 0, 3: 0 }, theoTrangThai: {} };
  for (const x of ds || []) {
    kq.tong += 1;
    kq.theoDot[x.dot || 2] += 1;
    const tt = x.trangThai || 'chua-tiep-can';
    kq.theoTrangThai[tt] = (kq.theoTrangThai[tt] || 0) + 1;
    if (DANG_THEO.has(tt)) kq.dangTheo += 1;
  }
  return kq;
}

/**
 * Nhắc nhở dựa trên chính danh sách. Mỗi nhắc nhở là một câu hành động được,
 * không phải lời khen chê.
 *
 * @param {Array} ds     danh sách đã lưu
 * @param {Array} funds  danh mục quỹ hiện hành, để biết quỹ nào đã gỡ khỏi danh mục
 */
export function nhacNho(ds, funds = []) {
  const ra = [];
  const tk = thongKe(ds);
  if (!tk.tong) return ra;
  if (tk.dangTheo > GOI_Y_TOI_DA) {
    ra.push(`Bạn đang theo đuổi ${tk.dangTheo} nơi. Nên giữ khoảng 15 đến 20 để chuẩn bị kỹ cho từng nơi; chuyển những nơi ít phù hợp sang Tạm dừng.`);
  }
  if (tk.tong >= 5 && tk.theoDot[1] === 0) {
    ra.push('Chưa có nơi nào ở Đợt 1. Hãy chọn vài nơi ít ưu tiên để luyện câu chuyện và nghe phản hồi trước khi gặp nơi bạn muốn nhất.');
  }
  if (tk.theoDot[3] > Math.max(5, Math.ceil(tk.tong / 2))) {
    ra.push(`Có ${tk.theoDot[3]} nơi ở Đợt 3. Nếu nơi nào cũng là ưu tiên nhất thì không còn ưu tiên; giữ Đợt 3 cho khoảng 5 nơi bạn thật sự muốn.`);
  }
  const canGioiThieu = (ds || []).filter((x) => x.dot === 3 && DANG_THEO.has(x.trangThai || 'chua-tiep-can')
    && !x.nguoiGioiThieu && ['chua-tiep-can', 'tim-gioi-thieu'].includes(x.trangThai || 'chua-tiep-can'));
  if (canGioiThieu.length) {
    ra.push(`${canGioiThieu.length} nơi ở Đợt 3 chưa có người giới thiệu. Một lời giới thiệu từ founder họ đã đầu tư hiệu quả hơn nhiều so với email gửi hộp thư chung.`);
  }
  if (funds.length) {
    const conTrongDanhMuc = new Set(funds.map((f) => f.fundId));
    const daGo = (ds || []).filter((x) => !conTrongDanhMuc.has(x.id));
    if (daGo.length) {
      ra.push(`${daGo.map((x) => x.name).join(', ')} không còn trong danh mục. Kiểm tra lại trên trang chính thức xem nơi này còn đầu tư không.`);
    }
  }
  return ra;
}
